import { useRouter } from 'next/router';
import React, { useRef, useState } from 'react';
import { Article } from '~/lib';
import { Link, Image, AspectRatio, Button } from '~/shared/components';
import Flex from '~/shared/components/Flex';
import { weakKey } from '~/shared/utils/jsx';
import { useTranslation } from '~/shared/utils/translation';
import {
    Headline,
    StyledCategory,
    StyledImageContainer,
    StyledInfoContainer,
    SubHeadline,
    CategoryTag,
    StyledArticle,
    StyledArticleContainer,
} from './styled';

type props = {
    articles: Article[];
    page?: number;
};

export const ArticleCard = ({
    article: item,
    priority,
    variant,
    className,
}: {
    article: Article;
    priority: boolean;
    variant?: 'default' | 'compact';
    className?: string;
}) => {
    const isCompact = variant === 'compact';
    const imageAltText = item?.pageImageAltText?.length ? item?.pageImageAltText : item.pageTitle;

    return (
        <StyledCategory className={className}>
            <Link noHover href={item.url} title={imageAltText}>
                {item.pageImage?.src ? (
                    isCompact ? (
                        <AspectRatio ratio={1}>
                            <StyledImageContainer>
                                <Image
                                    src={item.pageImage.src}
                                    alt={imageAltText}
                                    priority={priority}
                                    layout="responsive"
                                    height={456}
                                    width={367}
                                    objectPosition="center"
                                    objectFit="cover"
                                />
                            </StyledImageContainer>
                        </AspectRatio>
                    ) : (
                        <StyledImageContainer>
                            <Image
                                src={item.pageImage.src}
                                alt={imageAltText}
                                priority={priority}
                                layout="responsive"
                                height={456}
                                width={367}
                                objectPosition="center"
                                objectFit="cover"
                            />
                        </StyledImageContainer>
                    )
                ) : null}
                <StyledInfoContainer>
                    {!isCompact && <CategoryTag variant="bodySmall">{item.parentName}</CategoryTag>}
                    <Headline variant={isCompact ? 'display4' : 'display3'}>
                        {item.pageTitle}
                    </Headline>
                    {!isCompact && <SubHeadline variant="body">{item.pageDescription}</SubHeadline>}
                </StyledInfoContainer>
            </Link>
        </StyledCategory>
    );
};

export const Articles = (props: props) => {
    const { query } = useRouter();
    const pageIndex = (query.page as string) ? parseInt(query.page as string) : 1;
    const [page, setPage] = useState(pageIndex);
    const defaultCount = 20;
    const { translate } = useTranslation();
    const { asPath, push } = useRouter();
    const refId = useRef('');

    const articles = props.articles?.slice(0, defaultCount * (page - 1) + defaultCount);

    return (
        <>
            <StyledArticle data-testid="articles-section">
                {articles.map((item, index) => {
                    if (index === articles.length - 1) {
                        refId.current = 'articleId' + index;
                    }

                    return (
                        <StyledArticleContainer
                            data-testid="article-item"
                            key={weakKey(item)}
                            id={'articleId' + index}
                        >
                            <ArticleCard article={item} priority={index < 8} />
                        </StyledArticleContainer>
                    );
                })}
            </StyledArticle>

            {props.articles.length > defaultCount * page && (
                <Flex justifyContent="center">
                    <Button
                        as={Link}
                        href={asPath.split('?')[0] + '?page=' + (page + 1)}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPage(page + 1);
                            push(asPath.split('?')[0] + '?page=' + (page + 1), undefined, {
                                shallow: true,
                            });
                            document
                                .querySelector<HTMLInputElement>(`#${refId.current} a`)
                                ?.focus();
                        }}
                    >
                        {translate('plp.loadMoreText', { amount: defaultCount.toString() })}
                    </Button>
                </Flex>
            )}
        </>
    );
};
